import { Controller } from '@hotwired/stimulus';
import Tab from 'bootstrap/js/src/tab';
import { t } from '../javascript/lib/i18n';

const CLASS_HIDDEN = 'd-none';
const CLASS_NEW_ACTIVITY = 'amba-tab--new-activity';
// Matches open alert rows only — excludes the toggle ("Show X more") row and closed alerts
const ALERT_ROW_SELECTOR = 'tr:not([data-role="toggle"]):not([data-closed])';
// Matches all alert rows including closed — used to determine whether to keep the tab visible
const ALL_ROW_SELECTOR = 'tr:not([data-role="toggle"])';
// The card list (shown below sm) mirrors the table's alerts as direct-child
// divs, plus a toggle. Open cards drive truncation exactly like the rows above.
const OPEN_CARD_SELECTOR =
  ':scope > :not([data-role="toggle"]):not([data-closed])';

// Reactively manages the "Immediate action required" section in response to
// Turbo Stream row additions/replacements. Handles badge counts, tab visibility,
// section visibility, row/card truncation (first N visible, rest collapsed), and
// "new activity" indicators on inactive tabs when alerts stream in.
//
// Badge counts and tab/section visibility are computed solely from the table
// (the tbody rows) as the single source of truth. The card list (below sm) is a
// separate DOM subtree: it is truncated independently from its own cards, so it
// only stays consistent with the table as long as the alert broadcasts keep the
// two in sync (each row stream has a matching card stream). If a broadcast ever
// updates the table without the card, the badge/tabs stay correct but the card
// list can truncate differently — nothing here reconciles the two.
export default class extends Controller {
  static targets = [
    'tab',
    'badge',
    'tbody',
    'cards',
    'dropdownItem',
    'scroller',
  ];
  static values = {
    visibleRowCount: {
      type: Number,
      default: 5,
    },
  };

  connect() {
    this.observer = new MutationObserver((mutations) => {
      const affectedTileKeys = new Set();
      const tileKeysWithNewRows = new Set();

      for (const mutation of mutations) {
        // A mutation can land in either the table (tbody) or the card list —
        // both carry data-tile-key, so resolve from the nearest tagged ancestor.
        const tileKey =
          mutation.target.closest('[data-tile-key]')?.dataset.tileKey;
        if (!tileKey) continue;

        affectedTileKeys.add(tileKey);

        // Track which categories had new rows added. Excludes replacements
        // (which have both addedNodes and removedNodes) so closing an alert —
        // which replaces the row in place — doesn't re-trigger "new activity".
        if (
          mutation.type === 'childList' &&
          mutation.addedNodes.length > 0 &&
          mutation.removedNodes.length === 0
        ) {
          tileKeysWithNewRows.add(tileKey);
        }
      }

      // Pause observer while updating DOM to avoid infinite loops
      this.#withoutObserver(() => {
        for (const key of affectedTileKeys) {
          this.#updateCategory(key);
        }
        for (const key of tileKeysWithNewRows) {
          this.#showNewActivityIfInactive(key);
        }
        this.#updateSectionVisibility();
      });
    });

    this.#observeAll();
    this.#withoutObserver(() => this.#updateAll());

    // Clear "new activity" badge when a tab is shown
    this.element
      .querySelector('[role="tablist"]')
      ?.addEventListener('shown.bs.tab', this.#clearNewActivity);
  }

  disconnect() {
    this.observer.disconnect();
    this.element
      .querySelector('[role="tablist"]')
      ?.removeEventListener('shown.bs.tab', this.#clearNewActivity);
  }

  #observeAll() {
    [...this.tbodyTargets, ...this.cardsTargets].forEach((container) => {
      this.observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-closed'],
      });
    });
  }

  // Disconnects observer, runs callback, reconnects. Prevents our own DOM
  // mutations (class toggling, target assignment) from re-triggering the observer.
  #withoutObserver(callback) {
    this.observer.disconnect();
    callback();
    this.#observeAll();
  }

  #updateAll() {
    const keys = this.tbodyTargets.map((t) => t.dataset.tileKey);
    for (const key of keys) {
      this.#updateCategory(key);
    }
    this.#updateSectionVisibility();
  }

  #updateCategory(tileKey) {
    const tbody = this.tbodyTargets.find((t) => t.dataset.tileKey === tileKey);
    if (!tbody) return;

    const alertRows = [...tbody.querySelectorAll(ALERT_ROW_SELECTOR)];
    const openCount = alertRows.length;
    const totalCount = tbody.querySelectorAll(ALL_ROW_SELECTOR).length;

    this.#updateBadge(tileKey, openCount);
    this.#updateTabVisibility(tileKey, totalCount);
    this.#updateTruncation(tbody, alertRows);

    // Mirror the truncation onto the card list (below sm) so both views cap at
    // the same N and share a "Show more" toggle.
    const cards = this.cardsTargets.find((c) => c.dataset.tileKey === tileKey);
    if (cards) {
      this.#updateTruncation(cards, [
        ...cards.querySelectorAll(OPEN_CARD_SELECTOR),
      ]);
    }
  }

  // Updates every badge for the tile key — there's one on the tab and one on
  // the matching dropdown item, and both need to stay in sync.
  #updateBadge(tileKey, count) {
    const badges = this.badgeTargets.filter(
      (b) => b.dataset.tileKey === tileKey
    );
    for (const badge of badges) {
      badge.classList.toggle(CLASS_HIDDEN, count === 0);
      badge.textContent = count > 10 ? '10+' : count;
    }
  }

  // Hides the tab only when there are no rows at all (open or closed).
  // Keeps it visible with a hidden badge while closed rows remain in the table.
  // Mirrors the same visibility onto the dropdown item so the "all alerts"
  // menu only lists categories that actually have rows.
  #updateTabVisibility(tileKey, totalCount) {
    const tab = this.tabTargets.find((t) => t.dataset.tileKey === tileKey);
    if (tab) tab.classList.toggle(CLASS_HIDDEN, totalCount === 0);

    const item = this.dropdownItemTargets.find(
      (i) => i.dataset.tileKey === tileKey
    );
    if (item) item.classList.toggle(CLASS_HIDDEN, totalCount === 0);
  }

  // Activates the matching tab when its dropdown entry is chosen. Delegates to
  // Bootstrap's Tab so the real tablist stays the single source of truth for
  // active state, the shown.bs.tab event, and new-activity clearing.
  showTab(event) {
    const { tileKey } = event.currentTarget.dataset;
    const tab = this.tabTargets.find((t) => t.dataset.tileKey === tileKey);
    const button = tab?.querySelector('[role="tab"]');
    if (button) Tab.getOrCreateInstance(button).show();
  }

  // Ensures only the first N rows are visible. Rows beyond that get the
  // table-row-collapse target so the toggle button can expand/collapse them.
  // Respects the current expand/collapse state (aria-expanded on toggle button).
  #updateTruncation(tbody, alertRows) {
    const overflowCount = alertRows.length - this.visibleRowCountValue;
    const toggleRow = tbody.querySelector('[data-role="toggle"]');

    if (overflowCount <= 0) {
      alertRows.forEach((row) => {
        row.classList.remove(CLASS_HIDDEN);
        delete row.dataset.tableRowCollapseTarget;
      });
      if (toggleRow) toggleRow.classList.add(CLASS_HIDDEN);
      return;
    }

    if (toggleRow) toggleRow.classList.remove(CLASS_HIDDEN);

    const toggleBtn = toggleRow?.querySelector('button');
    const isExpanded = toggleBtn?.getAttribute('aria-expanded') === 'true';

    alertRows.forEach((row, index) => {
      if (index >= this.visibleRowCountValue) {
        row.dataset.tableRowCollapseTarget = 'row';
        row.classList.toggle(CLASS_HIDDEN, !isExpanded);
      } else {
        delete row.dataset.tableRowCollapseTarget;
        row.classList.remove(CLASS_HIDDEN);
      }
    });

    const showMoreText = toggleRow?.querySelector(
      '[data-table-row-collapse-target="showMoreText"]'
    );
    if (showMoreText) {
      showMoreText.textContent = t(
        'js.immediate_action_required.show_more_alerts',
        { count: overflowCount }
      );
    }
  }

  // Hides the entire section when no categories have open alerts,
  // shows it and activates the tab if there's only one visible.
  #updateSectionVisibility() {
    const visibleTabs = this.tabTargets.filter(
      (tab) => !tab.classList.contains(CLASS_HIDDEN)
    );
    this.element.classList.toggle(CLASS_HIDDEN, visibleTabs.length === 0);

    if (visibleTabs.length === 1) {
      const button = visibleTabs[0].querySelector('[role="tab"]');
      if (button) Tab.getOrCreateInstance(button).show();
    }

    this.#refreshOverflow();
  }

  // Showing/hiding tabs (or revealing the whole section, which starts as
  // display:none) changes whether the tab row overflows — but the
  // overflow-detector only recomputes on window resize and on its own connect.
  // Nudge it directly so the "all alerts" dropdown reveals/hides correctly.
  // Kept here rather than in overflow-detector to leave that utility general.
  #refreshOverflow() {
    if (!this.hasScrollerTarget) return;

    requestAnimationFrame(() => {
      this.application
        .getControllerForElementAndIdentifier(
          this.scrollerTarget,
          'overflow-detector'
        )
        ?.updateOverflowState();
    });
  }

  // Shows "New activity" badge on a tab if it's not currently active
  #showNewActivityIfInactive(tileKey) {
    const tab = this.tabTargets.find((t) => t.dataset.tileKey === tileKey);
    if (!tab) return;

    const isActive = tab.querySelector('[role="tab"].active');
    if (!isActive) {
      tab.classList.add(CLASS_NEW_ACTIVITY);
    }
  }

  // Clears "New activity" badge when the user switches to a tab
  #clearNewActivity = (event) => {
    const tab = event.target.closest(
      '[data-immediate-action-required-target="tab"]'
    );
    if (tab) {
      tab.classList.remove(CLASS_NEW_ACTIVITY);
    }
  };
}
